/**
 * @file        loading.service.ts
 * @description Tracks pending work for the shared loading overlay
 * @version     1.0.0
 * @author      Boris Veits
 * @copyright   Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *              Property of MindShare HDV LLC.  This file cannot be used or
 *              altered in anyway without expressed written permission from
 *              MindShare HDV LLC.
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { LOADING_SHOW_DELAY_MS } from '@app/constants/Loading.constant';

@Injectable
({
    providedIn: 'root'
})
class LoadingService
{
    public loading$: Observable<boolean>;

    private pendingCount: number;
    private showTimer: ReturnType<typeof setTimeout> | null;
    private loadingSubject: BehaviorSubject<boolean>;

    /**
     * @desc   - initialize the pending counter and overlay state
     * @access - public
     * @param  -
     * @return -
     */
    constructor()
    {
        this.pendingCount = 0;
        this.showTimer = null;
        this.loadingSubject = new BehaviorSubject<boolean>(false);
        this.loading$ = this.loadingSubject.asObservable();
    }

    /**
     * @desc   - stop a pending delayed show
     * @access - private
     * @param  -
     * @return - void
     */
    private clearShowTimer(): void
    {
        if (this.showTimer)
        {
            clearTimeout(this.showTimer);
            this.showTimer = null;
        }
    }

    /**
     * @desc   - register pending work; the overlay appears after the
     *           delay unless all work finishes first
     * @access - public
     * @param  - number delayMs : delay before showing (0 shows now)
     * @return - void
     */
    public show
    (
        delayMs: number = LOADING_SHOW_DELAY_MS
    ): void
    {
        this.pendingCount++;

        if (delayMs <= 0)
        {
            this.clearShowTimer();
            this.loadingSubject.next(true);
        }
        else if (!this.loadingSubject.value && !this.showTimer)
        {
            this.showTimer = setTimeout(() =>
            {
                this.showTimer = null;
                if (this.pendingCount > 0)
                {
                    this.loadingSubject.next(true);
                }
            }, delayMs);
        }
    }

    /**
     * @desc   - finish one unit of pending work; hides the overlay when
     *           nothing is pending
     * @access - public
     * @param  -
     * @return - void
     */
    public hide(): void
    {
        if (this.pendingCount > 0)
        {
            this.pendingCount--;
        }

        if (this.pendingCount === 0)
        {
            this.clearShowTimer();
            this.loadingSubject.next(false);
        }
    }
}

export { LoadingService };
